import React, { useContext, useEffect, useState } from 'react'
import clsx from 'clsx'
import { fromUnixTime, format } from 'date-fns'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import Layout from '../components/Layout'
import { UserContext } from '../contexts/UserContext'
import CommitSVG from '../components/svg/CommitSVG'
import PaymentService from '../services/PaymentService'
import FailedSVG from '../components/svg/FailedSVG'
import { useSettings } from '../hooks/useSettings'
import useNotification from '../hooks/useNotification'
import CheckoutSessionReturn from './CheckoutSessionReturn'

// Ritorno dal vecchio flusso PaymentIntent + Payment Element
// (?payment_intent=…&redirect_status=…).
const PaymentIntentCompletion = () => {
  const { t } = useTranslation('payments')
  const [paymentDetails, setPaymentDetails] = useState(null)
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)

  const [searchParams] = useSearchParams()

  const navigate = useNavigate()

  const { error: showError } = useNotification()
  const { isDarkMode } = useSettings()
  const { user } = useContext(UserContext)

  const backToMainPage = () => {
    navigate('/')
  }

  useEffect(() => {
    const paymentIntent = searchParams.get('payment_intent')
    const clientSecret = searchParams.get('payment_intent_client_secret')
    const redirectStatus = searchParams.get('redirect_status')

    if (
      !paymentIntent ||
      !clientSecret ||
      !redirectStatus ||
      (redirectStatus !== 'failed' && redirectStatus !== 'succeeded')
    ) {
      setLoading(false)
      showError(t('toast.unknownStatus'))
      navigate('/')
      return
    } else {
      setStatus(redirectStatus)
    }

    PaymentService.getPaymentDetails(paymentIntent)
      .then((res) => {
        if (
          (redirectStatus === 'succeeded' &&
            res.data.payment_details &&
            res.data.payment_details?.status === 'succeeded') ||
          (redirectStatus === 'failed' &&
            res.data.payment_details &&
            res.data.payment_details?.status !== 'succeeded')
        ) {
          setPaymentDetails(res.data.payment_details)
          if (
            redirectStatus === 'succeeded' &&
            res.data.payment_details &&
            res.data.payment_details?.status === 'succeeded'
          ) {
            // Il server verifica il PaymentIntent su Stripe e accredita
            // (importo e crediti derivati server-side): inviamo solo l'id.
            PaymentService.addCredits(paymentIntent)
              .then()
              .catch((e) => console.error(e?.data?.response))
          }
        } else {
          console.error('Payment status error')
          showError(t('toast.statusError'))
          navigate('/')
        }
      })
      .catch((e) => {
        console.error('Payment failed:', e?.response?.data?.message)
        showError(e?.response?.data?.message || t('toast.statusError'))
      })
      .finally(() => setLoading(false))
  }, [searchParams, navigate, showError, t, user.email])

  return (
    <div
      className={clsx(
        'relative w-screen h-screen overflow-hidden transition duration-200',
        {
          'bg-dark-blue text-white': isDarkMode,
          'bg-light-blue text-mid-blue': !isDarkMode,
        }
      )}
    >
      <Layout>
        <main className="h-[calc(100svh-30px)] desktop:h-[calc(100svh-54px)] flex justify-center items-center">
          {loading && (
            <img className="mx-auto" src="/assets/images/loader.svg" alt="" />
          )}
          {!loading &&
            status === 'succeeded' &&
            paymentDetails &&
            paymentDetails?.status === 'succeeded' && (
              <div
                className={clsx(
                  'w-[350px] h-[540px] bg-mid-blue rounded-lg p-6 flex flex-col justify-between items-center shadow-[4px_4px_10px_0px_#00000040]',
                  {
                    'bg-mid-blue': isDarkMode,
                    'bg-white': !isDarkMode,
                  }
                )}
              >
                <div className="mt-8 flex flex-col justify-center">
                  <CommitSVG
                    styles="w-[40px] h-[40px] self-center"
                    isBlueColor={!isDarkMode}
                  />
                  <h1 className="mt-4 text-xl tracking-wide">
                    {t('success.heading')}
                  </h1>
                </div>
                <div className="w-full">
                  <p className="text-sm mb-2">{t('success.section.details')}</p>
                  <ul className="rounded-lg border border-blue/70">
                    <li className="flex justify-between items-center gap-4 text-sm font-light border-b border-blue/70 py-2 px-2">
                      <p>{t('success.label.date')}</p>
                      <p className="truncate">
                        {format(
                          fromUnixTime(paymentDetails.created),
                          'dd.MM.yyyy HH:mm:ss'
                        )}
                      </p>
                    </li>
                    <li className="flex justify-between items-center gap-4 text-sm font-light border-b border-blue/70 py-2 px-2">
                      <p>{t('success.label.method')}</p>
                      <p className="truncate">
                        {paymentDetails.payment_method.charAt(0).toUpperCase() +
                          paymentDetails.payment_method.slice(1)}
                      </p>
                    </li>
                    <li className="flex justify-between items-center gap-4 text-sm font-light border-b border-blue/70 py-2 px-2">
                      <p>{t('success.label.payer')}</p>
                      <p className="truncate">{paymentDetails.payer}</p>
                    </li>
                    <li className="flex justify-between items-center gap-4 text-sm font-light border-b border-blue/70 py-2 px-2">
                      <p>{t('success.label.total')}</p>
                      <p className="truncate">
                        <span className="mr-px">
                          {paymentDetails.currency === 'eur' ? '€' : '$'}
                        </span>
                        {(paymentDetails.amount / 100).toFixed(2)}
                      </p>
                    </li>
                    <li className="flex justify-between items-center gap-4 text-sm font-light border-blue/70 py-2 px-2">
                      <p>{t('success.label.email')}</p>
                      <p className="truncate">{user.email}</p>
                    </li>
                  </ul>
                </div>
                <div className="w-full">
                  <button
                    className={clsx(
                      'h-[40px] w-full text-white text-sm rounded-xl hover:bg-blue-hover transition duration-100 active:shadow-[inset_0px_0px_14px_5px_#00000020] flex justify-center items-center',
                      {
                        'bg-blue': isDarkMode,
                        'bg-sky-blue': !isDarkMode,
                      }
                    )}
                    onClick={backToMainPage}
                  >
                    {t('success.button.back')}
                  </button>
                  <p
                    className={clsx('text-center text-2xs font-light mt-2', {
                      'text-white/80': isDarkMode,
                      'text-black/80': !isDarkMode,
                    })}
                  >
                    {t('success.info.processingTime')}
                  </p>
                </div>
              </div>
            )}
          {!loading &&
            status === 'failed' &&
            paymentDetails &&
            paymentDetails?.status !== 'succeeded' && (
              <div
                className={clsx(
                  'w-[350px] h-[350px] rounded-lg p-6 flex flex-col justify-between items-center shadow-[4px_4px_10px_0px_#00000040]',
                  {
                    'bg-mid-blue': isDarkMode,
                    'bg-white': !isDarkMode,
                  }
                )}
              >
                <div className="mt-8 flex flex-col justify-center">
                  <FailedSVG />
                  <h1 className="mt-4 text-xl tracking-wide">
                    {t('failed.heading')}
                  </h1>
                </div>
                <p className="w-full text-center font-light">
                  {paymentDetails.message}
                </p>
                <div className="w-full">
                  <button
                    className={clsx(
                      'h-[40px] w-full bg-blue text-white text-sm rounded-xl hover:bg-blue-hover transition duration-100 active:shadow-[inset_0px_0px_14px_5px_#00000020] flex justify-center items-center',
                      {
                        'bg-blue': isDarkMode,
                        'bg-sky-blue': !isDarkMode,
                      }
                    )}
                    onClick={backToMainPage}
                  >
                    {t('failed.button.back')}
                  </button>
                  <p
                    className={clsx('text-center text-2xs font-light mt-2', {
                      'text-white/80': isDarkMode,
                      'text-black/80': !isDarkMode,
                    })}
                  >
                    {t('failed.info.tryAgain')}
                  </p>
                </div>
              </div>
            )}
        </main>
      </Layout>
    </div>
  )
}

/**
 * Pagina di ritorno dei pagamenti. Con Managed Payments (Checkout incorporata)
 * Stripe torna con ?session_id=…; il vecchio flusso con ?payment_intent=….
 */
const PaymentCompletion = () => {
  const [searchParams] = useSearchParams()
  const sessionId = searchParams.get('session_id')
  return sessionId ? (
    <CheckoutSessionReturn sessionId={sessionId} />
  ) : (
    <PaymentIntentCompletion />
  )
}

export default PaymentCompletion
